import React, { useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { formatDate } from "../../../shared/utils/formatDate";
import TabDadosPrincipais from "../components/tabs/TabDadosPrincipais";
import TabHistoricoInteracoes from "../components/tabs/TabHistoricoInteracoes";
import TabUltimosExames from "../components/tabs/TabUltimosExames";
import TabHistoricoOrganograma from "../components/tabs/TabHistoricoOrganograma";
import TabJornadaPaciente from "../components/tabs/TabJornadaPaciente";
import { TAB_MENU_ITEMS } from "../constants/tiposInteracao";
import useTabs from "../hooks/useTabs";
import usePacienteRastreamento from "../hooks/usePacienteRastreamento";
import useHistoricoInteracoes from "../hooks/useHistoricoInteracoes";
import useProcessarPaciente from "../hooks/useProcessarPaciente";

const EditRastreamento = ({
  pacienteSelecionado,
  onClose,
  onPacienteProcessado,
}) => {
  const [tipoInteracaoSelecionado, setTipoInteracaoSelecionado] = useState(null);
  const [dataInteracao, setDataInteracao] = useState("");
  const [tipoRastreio, setTipoRastreio] = useState(null);
  const [observacao, setObservacao] = useState("");
  const [procedimento, setProcedimento] = useState("");
  const [resultado, setResultado] = useState("");
  const [dataObito, setDataObito] = useState("");
  const [tipoInteracaoId, setTipoInteracaoId] = useState(null);
  const [canalInteracaoSelecionado, setCanalInteracaoSelecionado] = useState(null);

  const { paciente, dadosCompletos, fetchDadosAtualizados } =
    usePacienteRastreamento(pacienteSelecionado);

  const { historico, setHistorico } = useHistoricoInteracoes(paciente?.id);

  const { processando, mensagem, handleProcessarPaciente } = useProcessarPaciente({
    pacienteId: paciente?.id,
    onPacienteProcessado,
    onDadosAtualizados: fetchDadosAtualizados,
  });

  useTabs({
    modalId: "edit-rastreamento",
    onClose,
  });

  const handleInteracaoSalva = () => {
    fetchDadosAtualizados();
  };

  return (
    <div
      className="modal fade"
      id="edit-rastreamento"
      tabIndex="-1"
      aria-labelledby="modalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h4>Visualizar dados da paciente</h4>
            <button
              type="button"
              className="close"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <span aria-hidden="true">x</span>
            </button>
          </div>

          <div className="modal-body">
            <ul
              className="nav nav-tabs tab-style-1 d-sm-flex d-block"
              role="tablist"
            >
              {TAB_MENU_ITEMS.map((tab, index) => (
                <li className="nav-item" key={tab.id}>
                  <Link
                    className={`nav-link ${index === 0 ? "active" : ""}`}
                    aria-current={index === 0 ? "page" : undefined}
                    data-bs-toggle="tab"
                    to={tab.target}
                  >
                    {tab.label}
                  </Link>
                </li>
              ))}

              <li className="nav-item d-flex justify-content-end align-items-center ms-auto">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleProcessarPaciente}
                  disabled={processando}
                >
                  {processando ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-1"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Processando...
                    </>
                  ) : (
                    <>
                      <i className="feather-play me-1"></i>
                      Processar
                    </>
                  )}
                </button>
              </li>
            </ul>

            {(mensagem || dadosCompletos?.rastreamentoPacienteOrganizado?.dataUltimoProcessamento) && (
              <div className="d-flex flex-wrap align-items-center gap-3 mt-3 mb-2 p-2 rounded-3 bg-light border">
                {dadosCompletos?.rastreamentoPacienteOrganizado?.dataUltimoProcessamento && (
                  <span className="text-muted small">
                    Ultimo processamento: {" "}
                    {formatDate(
                      dadosCompletos?.rastreamentoPacienteOrganizado
                        ?.dataUltimoProcessamento,
                    ) || "-"}
                  </span>
                )}

                {mensagem && (
                  <span
                    className={`badge ${
                      mensagem.includes("sucesso") ? "bg-success" : "bg-danger"
                    }`}
                  >
                    {mensagem}
                  </span>
                )}
              </div>
            )}

            <div className="tab-content mt-3">
              <div className="tab-pane fade show active" id="dados-principais">
                <TabDadosPrincipais dadosCompletos={dadosCompletos} />
              </div>

              <div className="tab-pane fade" id="historico-interacoes">
                <TabHistoricoInteracoes
                  paciente={paciente}
                  tipoInteracaoSelecionado={tipoInteracaoSelecionado}
                  setTipoInteracaoSelecionado={setTipoInteracaoSelecionado}
                  dataInteracao={dataInteracao}
                  setDataInteracao={setDataInteracao}
                  tipoRastreio={tipoRastreio}
                  setTipoRastreio={setTipoRastreio}
                  observacao={observacao}
                  procedimento={procedimento}
                  setProcedimento={setProcedimento}
                  setObservacao={setObservacao}
                  resultado={resultado || ""}
                  setResultado={setResultado}
                  dataObito={dataObito}
                  setDataObito={setDataObito}
                  tipoInteracaoId={tipoInteracaoId}
                  setTipoInteracaoId={setTipoInteracaoId}
                  canalInteracaoSelecionado={canalInteracaoSelecionado}
                  setCanalInteracaoSelecionado={setCanalInteracaoSelecionado}
                  historico={historico}
                  setHistorico={setHistorico}
                  dadosCompletos={dadosCompletos}
                  onInteracaoSalva={handleInteracaoSalva}
                  onProcessarPaciente={handleProcessarPaciente}
                />
              </div>

              <div className="tab-pane fade" id="ultimos-exames">
                <TabUltimosExames paciente={paciente} dadosCompletos={dadosCompletos} />
              </div>

              <div className="tab-pane fade" id="historico-organograma">
                <TabHistoricoOrganograma
                  patientId={dadosCompletos?.id}
                  paciente={paciente}
                  dadosCompletos={dadosCompletos}
                />
              </div>

              <div className="tab-pane fade" id="jornada-paciente">
                <TabJornadaPaciente
                  patientId={dadosCompletos?.id}
                  paciente={paciente}
                  dadosCompletos={dadosCompletos}
                />
              </div>
            </div>
          </div>

          <div className="modal-footer border-top d-flex justify-content-end">
            <button
              className="btn btn-secondary"
              type="button"
              data-bs-dismiss="modal"
            >
              <i className="feather-x me-2"></i>
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

EditRastreamento.propTypes = {
  pacienteSelecionado: PropTypes.object,
  onClose: PropTypes.func,
  onPacienteProcessado: PropTypes.func,
};

export default EditRastreamento;
